import React, { useState, useRef, useEffect } from 'react';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import { GridFooterContainer } from '@mui/x-data-grid';
import { Box, Button, DialogContent, Select, MenuItem, Popover, Typography, FormControl, Pagination, PaginationItem, IconButton } from '@mui/material';
import Papa from 'papaparse';
import { useFormik } from 'formik';
import '../components/DeviceTable.css';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/system';
import CustomTextField from '../components/CustomTextField';
import CustomSelect from '../components/CustomSelect';
import DeviceManagementDialog from './DeviceManagementDialog'; // Import your custom dialog component
import koKR from '../components/koKR.json'; // Import the translation file
import CustomColumnSortedAscendingIcon from '../components/CustomColumnSortedAscendingIcon';
import CustomColumnSortedDescendingIcon from '../components/CustomColumnSortedDescendingIcon';
import apiClient from '../api/apiClient'; // API client import
import Config from '../Config'; // apiUrl 추가
import { getAccessToken } from '../utils/token';

const apiUrl = Config.apiUrl;

function CustomPagination(props) {
    const pageCount = Math.ceil(props.totalElements / props.size);
    const rowsPerPage = props.size;

    const handlePageChange = (event, value) => {
        props.onPageChange(value - 1); // DataGrid는 0-based page, Pagination은 1-based page
    };

    const handlePageSizeChange = (event) => {
        props.onPageSizeChange(parseInt(event.target.value, 10));
    };

    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 16px', width: '100%', marginBottom: '25px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1">Showing</Typography>
                <FormControl variant="outlined" sx={{ mx: 1, minWidth: 78, width: 78, height: 38 }}>
                    <Select value={rowsPerPage} onChange={handlePageSizeChange}
                        disableUnderline
                        sx={{ fontSize: '0.875rem', height: '100%', paddingTop: '8px', paddingBottom: '8px' }}>
                        {[10, 20, 30, 50].map((size) => (
                            <MenuItem key={size} value={size}>
                                {size}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography variant="body1">of {props.totalElements}</Typography>
            </Box>
            {props.totalElements > 0 && (
                <Pagination
                    count={pageCount}
                    page={props.page + 1}
                    onChange={handlePageChange}
                    siblingCount={1}
                    boundaryCount={1}
                    shape="rounded"
                    color="primary"
                    variant='outlined'
                    renderItem={(item) => (
                        <PaginationItem
                            {...item}
                            sx={{
                                '&.Mui-selected': {
                                    backgroundColor: '#1976d2',
                                    color: '#fff',
                                },
                                backgroundColor: item.selected ? 'initial' : '#e0e0e0',
                                color: item.selected ? '#fff' : '#8b909a', // 선택되지 않았을 때 글자색 설정
                                border: 'none',
                                width: '28px',    // 가로 크기 설정
                                height: '28px',   // 세로 크기 설정
                                margin: '1px',    // 박스 간 간격 설정 (전체 간격이 2px이므로 양쪽에 1px씩)
                            }}
                        />
                    )}
                />
            )}
        </Box>
    );
}

const NoRowsOverlay = styled('div')({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    fontSize: '1.5rem',
    color: '#888',
    flexDirection: 'column',
});

function CustomFooter(props) {
    return (
        <GridFooterContainer sx={{ borderTop: 'none', color: '#8b909a' }}>
            <CustomPagination
                page={props.page}
                size={props.size}
                totalElements={props.totalElements}
                onPageChange={props.onPageChange}
                onPageSizeChange={props.onPageSizeChange}
            />
        </GridFooterContainer>
    );
}

const DeviceList = () => {
    const [rows, setRows] = useState([]);
    const [openFilterDialog, setOpenFilterDialog] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const apiRef = useGridApiRef();
    const fileInputRef = useRef(null);
    const { i18n } = useTranslation();
    const { t } = useTranslation('console');
    const getRowHeight = (params) => 58;
    const [selectionModel, setSelectionModel] = React.useState([]);
    const [open, setOpen] = useState(false);
    const [selectedRowId, setSelectedRowId] = useState(null);

    // Pagination and sorting states
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0); // 0-based page index
    const [size, setSize] = useState(10); // Page size
    const [sort, setSort] = useState(['createdAt,desc']); // Default sort
    const [totalElements, setTotalElements] = useState(0); // Total number of elements for pagination

    // Filter states
    const [filterRequest, setFilterRequest] = useState({
        prodName: '',
        serial: '',
        country: 0,
        area: 0,
        reseller: 0,
        manager: 0,
    });

    const [productsMenuItems, setProductsMenuItems] = useState([]); // Products menu items
    const [countryMenuItems, setCountryMenuItems] = useState([]); // Country menu items
    const [resellerMenuItems, setResellerMenuItems] = useState([]); // Reseller menu items
    const [managerMenuItems, setManagerMenuItems] = useState([]); // Manager menu items
    const [areaMenuItems, setAreaMenuItems] = useState([]); // Area menu items

    const handleIconClick = (rowId) => {
        console.log('handleIconClick called with rowId:', rowId); // Debugging log
        console.log('rowId type:', typeof rowId); // Debugging log
        setSelectedRowId(rowId);
        console.log('Selected Row ID set to:', rowId); // Debugging log added
        setOpen(true);  // Open the DeviceManagementDialog
    };

    const handleClose = () => {
        setOpen(false);  // Close the DeviceManagementDialog
    };

    const columns = [
        { field: 'order', headerName: `${t('device_list.column.id')}`, flex: 1, minWidth: 70, headerAlign: 'center', align: 'center' },
        { field: 'country', headerName: `${t('device_list.column.country')}`, flex: 1, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'region', headerName: `${t('device_list.column.region')}`, flex: 1, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'reseller', headerName: `${t('device_list.column.reseller')}`, flex: 2.5, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'manager', headerName: `${t('device_list.column.manager')}`, flex: 1, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'serial', headerName: `${t('device_list.column.serial')}`, flex: 1.5, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'reg_date', headerName: `${t('device_list.column.reg_date')}`, flex: 2, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'connection_state', headerName: `${t('device_list.column.connection_state')}`, flex: 1, minWidth: 100, headerAlign: 'center', align: 'center', renderCell: (params) => renderConnection(params) },
        {
            field: 'management', headerName: `${t('device_list.column.management')}`, flex: 1, minWidth: 100, headerAlign: 'center', align: 'center',
            sortable: false,
            renderCell: (params) => {
                console.log('RenderCell params.id:', params.id); // Add this line for debugging
                return (
                    <IconButton
                        onClick={() => handleIconClick(params.id)}
                        // onMouseEnter={() => setHoveredRow(params.id)} // Set hovered row
                        // onMouseLeave={() => setHoveredRow(null)} // Remove hovered row
                    >
                        <img
                            src={'./button_setting_default.png'} // Change icon based on hover state
                            alt="custom icon"
                            style={{ width: 40, height: 40 }}
                        />
                    </IconButton>
                );
            },
        },
    ];

    const handleExport = () => {
        apiRef.current.exportDataAsCsv();
    };

    // API 데이터 호출
    const fetchDeviceList = async () => {
        setLoading(true);
        try {
            const token = getAccessToken();

            const requestParams = {};
            // filterRequest에서 값이 있는 필드만 추가
            if (filterRequest.prodName) requestParams.prodName = filterRequest.prodName;
            if (filterRequest.serial) requestParams.serial = filterRequest.serial;
            if (filterRequest.country !== 0) requestParams.country = filterRequest.country;
            if (filterRequest.area !== 0) requestParams.area = filterRequest.area;
            if (filterRequest.reseller !== 0) requestParams.reseller = filterRequest.reseller;
            if (filterRequest.manager !== 0) requestParams.manager = filterRequest.manager;

            const response = await apiClient.get(`${apiUrl}/console/devices`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: {
                    ...requestParams, // 필터링 파라미터
                    page: page,
                    size: size,
                    sort: sort, // 정렬 파라미터 (배열 그대로 전달)
                },
            });

            const data = response.data.content;

            // API 데이터 매핑
            const mappedRows = data.map((device) => ({
                id: device.id,
                order: device.order,
                country: device.country,
                region: device.area,
                reseller: device.reseller,
                manager: device.manager,
                serial: device.serial,
                reg_date: device.registeredAt,
                connection_state: device.isConnect,
                // management: 'management',
            }));

            setRows(mappedRows);
            setTotalElements(response.data.totalElements); // 총 데이터 수 설정
        } catch (error) {
            console.error('Error fetching devices:', error);
        } finally {
            setLoading(false);
        }
    };

    // API 데이터 호출 useEffect
    useEffect(() => {
        fetchDeviceList();
    }, [page, size, sort, filterRequest]); // filterRequest도 의존성 배열에 추가

    useEffect(() => {
        // 검사기 모델 데이터 불러오기
        const fetchProducts = async () => {
            try {
                const response = await apiClient.get(`${apiUrl}/console/devices/products`, {
                    headers: {
                        Authorization: `Bearer ${getAccessToken()}`,
                    },
                });

                const productsData = response.data.products.map((product) => ({
                    value: product.value,
                    label: product.text,
                }));

                setProductsMenuItems(productsData); // 받아온 products 데이터를 설정
            } catch (error) {
                console.error('Error fetching products:', error);
            }
        };

        // Country 데이터 불러오기
        const fetchCountries = async () => {
            try {
                const response = await apiClient.get(`${apiUrl}/console/devices/countries`, {
                    headers: {
                        Authorization: `Bearer ${getAccessToken()}`,
                    },
                });

                const countriesData = response.data.countries.map((country) => ({
                    value: country.value,
                    label: country.text,
                }));

                setCountryMenuItems(countriesData); // 받아온 countries 데이터를 설정
            } catch (error) {
                console.error('Error fetching countries:', error);
            }
        };

        // Resellers 데이터 불러오기
        const fetchResellers = async () => {
            try {
                const response = await apiClient.get(`${apiUrl}/console/devices/resellers`, {
                    headers: {
                        Authorization: `Bearer ${getAccessToken()}`,
                    },
                });

                const resellersData = response.data.resellers.map((reseller) => ({
                    value: reseller.value,
                    label: reseller.text,
                }));

                setResellerMenuItems(resellersData); // 받아온 resellers 데이터를 설정
            } catch (error) {
                console.error('Error fetching resellers:', error);
            }
        };

        // Managers 데이터 불러오기
        const fetchManagers = async () => {
            try {
                const response = await apiClient.get(`${apiUrl}/console/devices/managers`, {
                    headers: {
                        Authorization: `Bearer ${getAccessToken()}`,
                    },
                });

                const managersData = response.data.managers.map((manager) => ({
                    value: manager.value,
                    label: manager.text,
                }));

                setManagerMenuItems(managersData); // 받아온 managers 데이터를 설정
            } catch (error) {
                console.error('Error fetching managers:', error);
            }
        };

        // Areas 데이터 불러오기
        const fetchAreas = async () => {
            try {
                const response = await apiClient.get(`${apiUrl}/console/devices/areas`, {
                    headers: {
                        Authorization: `Bearer ${getAccessToken()}`,
                    },
                });

                const areasData = response.data.areas.map((area) => ({
                    value: area.value,
                    label: area.text,
                }));

                setAreaMenuItems(areasData); // 받아온 areas 데이터를 설정
            } catch (error) {
                console.error('Error fetching areas:', error);
            }
        };

        fetchProducts();
        fetchCountries();
        fetchResellers();
        fetchManagers();
        fetchAreas();
    }, []);

    const handleImportClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Parse CSV file and transform data into the required format
        Papa.parse(file, {
            header: true,
            dynamicTyping: true,
            complete: async (results) => {
                // Transform parsed data into the required JSON format
                const transformedData = results.data.map((row) => ({
                    serial: row.serial || 'default_serial',
                    mac: row.mac || 'default_mac',
                    deviceState: row.deviceState || 'default_state',
                    reseller: row.reseller || 'default_reseller'
                }));

                const formData = new FormData();
                formData.append('data', JSON.stringify(transformedData)); // Append JSON data as a string

                try {
                    const token = getAccessToken(); // Retrieve access token

                    // Send the POST request
                    const response = await apiClient.post(
                        `${apiUrl}/console/devices/import`,
                        formData,
                        {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'multipart/form-data'
                            }
                        }
                    );

                    console.log('Data imported successfully:', response.data);
                } catch (error) {
                    console.error('Error importing data:', error);
                }
            },
        });
    };

    // 'View All' 버튼 클릭 시 전체 데이터 복원
    const handleViewAll = () => {
        setFilterRequest({
            prodName: '',
            serial: '',
            country: 0,
            area: 0,
            reseller: 0,
            manager: 0,
        });
        setPage(0); // 첫 페이지로 이동
        formik.resetForm(); // 필터 검색 폼의 입력값과 선택값 초기화
    };

    // 필터 검색 폼의 onSubmit에 handleFilterSearch 연결
    const formik = useFormik({
        initialValues: {
            prodName: '',
            serial: '',
            country: 0,
            area: 0,
            reseller: 0,
            manager: 0,
        },
        onSubmit: (values) => {
            // 필터링 요청 파라미터 업데이트 및 페이지 0으로 초기화
            setFilterRequest({
                prodName: values.prodName,
                serial: values.serial,
                country: values.country,
                area: values.area,
                reseller: values.reseller,
                manager: values.manager,
            });
            setPage(0); // 검색 시 첫 페이지로 이동
            handleCloseFilterDialog(); // 필터 다이얼로그 닫기
        },
    });

    // 'Filter Search' 버튼 클릭 시 필터 다이얼로그 토글
    const handleFilterSearch = (event) => {
        setAnchorEl(event.currentTarget);
        setOpenFilterDialog(!openFilterDialog);
    };

    const handleCloseFilterDialog = () => {
        setOpenFilterDialog(false);
        setAnchorEl(null);
    };

    function renderConnection(params) {
        console.log(params);
        return (
            <Box sx={{ textAlign: 'center', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {params.value ? <img src="online.png" alt="online" /> : <img src="offline.png" alt="offline" />}
            </Box>
        );
    }

    const getLocaleText = () => {
        return i18n.language === 'ko' ? koKR : {};
    };

    return (
        <Box sx={{ width: '1592px', minWidth: 1024 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h2" sx={{ fontSize: '32px', color: '#002a70', fontWeight: 'bold', mt: '20px' }}>
                    {t('device_list.header.title')}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', width: '1524px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: '-10px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button
                            onClick={handleViewAll}
                            style={{
                                fontSize: '16px',
                                width: '178px',
                                backgroundColor: 'white',
                                border: '1px solid #ccc',
                                borderRadius: '12px 0 0 0',
                                height: '48px',
                                justifyContent: 'flex-start', // Align text to the left
                                paddingLeft: '16px', // Add some padding to the left
                                color: '#7d7d7d',
                                fontWeight: 'bold',
                            }}
                        >
                            {t('button.view_all')}
                        </Button>
                        <Button
                            onClick={handleFilterSearch}
                            style={{
                                fontSize: '16px',
                                width: '211px',
                                backgroundColor: 'white',
                                border: '1px solid #ccc',
                                borderRadius: '0 12px 0 0',
                                height: '48px',
                                justifyContent: 'flex-start', // Align text to the left
                                paddingLeft: '16px', // Add some padding to the left
                                paddingRight: '18px', // Add some padding to the right for endIcon
                                color: '#7d7d7d',
                                fontWeight: 'bold',
                            }}
                            startIcon={<img src={'/filter.png'} alt="Filter Icon" style={{ width: '30px', height: '30px' }} />}
                            endIcon={openFilterDialog ? <img src={'/up.png'} alt="Arrow Icon" style={{ width: '30px', height: '30px', marginLeft: '40px' }} /> :
                                <img src={'/down.png'} alt="Arrow Icon" style={{ width: '30px', height: '30px', marginLeft: '40px' }} />}
                        >
                            {t('button.filter_search')}
                        </Button>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '10px', width: 'calc(100% - 389px - 10px)' }}>
                    <Box className="button_container" sx={{ display: 'flex', gap: '10px' }}>
                        <Button
                            variant="outlined"
                            onClick={handleImportClick}
                            className="button_upload"
                            // endIcon={<img src={hovered ? '/upload-hover.png' : '/upload.png'} alt="Upload Icon" style={{ width: '30px', height: '30px' }} />}
                            // onMouseEnter={() => setHovered(true)} // Set hover to true on mouse enter
                            // onMouseLeave={() => setHovered(false)} // Set hover to false on mouse leave
                            endIcon={<img src={'/upload.png'} alt="Upload Icon" style={{ width: '30px', height: '30px' }} />}
                            sx={{
                                fontSize: '14px',
                                borderWidth: '2px',
                                borderRadius: '10px',
                                textTransform: 'none',
                                color: '#7d7d7d',
                                borderColor: '#7d7d7d',
                                fontWeight: '500px',
                                '&:hover': {
                                    borderWidth: '2px',
                                    backgroundColor: 'transparent',
                                    borderColor: '#007bfa',
                                    color: '#007bfa',
                                },
                            }}
                        >
                            {t('button.upload')}
                        </Button>
                        {/* Hidden file input element */}
                        <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={handleFileChange} // Handle file selection
                        />
                        <Button
                            variant="contained"
                            onClick={handleExport}
                            className="button_dwload"
                            sx={{
                                fontSize: '14px',
                                borderRadius: '10px',
                                textTransform: 'none',
                                backgroundColor: '#80befc',
                                '&:hover': {
                                    backgroundColor: '#0b5cd5',
                                },
                            }}
                            endIcon={<img src={'/download.png'} alt="Download Icon" style={{ width: '30px', height: '30px' }} />}
                        >
                            {t('button.download')}
                        </Button>
                    </Box>
                </Box>
            </Box>
            <Box sx={{ width: '1524px', height: '756px', mt: 0 }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    apiRef={apiRef}
                    getRowId={(row) => row.id}
                    getRowHeight={getRowHeight}
                    headerHeight={48}
                    localeText={getLocaleText()} // Use the localeText based on the current locale
                    onRowClick={handleIconClick}  // 행 클릭 시 이벤트 핸들러 호출
                    loading={loading} // DataGrid의 loading prop을 활성화
                    rowCount={totalElements}
                    pageSizeOptions={[10, 20, 30, 50]}
                    paginationModel={{ page, pageSize: size }}
                    onPaginationModelChange={(newModel) => {
                        setPage(newModel.page);
                        setSize(newModel.pageSize);
                    }}
                    sortingMode="server"
                    sortModel={sort.map(s => ({ field: s.split(',')[0], sort: s.split(',')[1] }))}
                    onSortModelChange={(newSortModel) => {
                        setSort(newSortModel.map(s => `${s.field},${s.sort}`));
                    }}
                    paginationMode="server"
                    slots={{
                        footer: () => (
                            <CustomFooter
                                page={page}
                                size={size}
                                totalElements={totalElements}
                                onPageChange={setPage}
                                onPageSizeChange={setSize}
                            />
                        ),
                        noRowsOverlay: () => (loading ? null : ( // 로딩 중일 때는 noRowsOverlay를 숨김
                            <NoRowsOverlay>
                                <img src="nodata.png" alt="No data" />
                            </NoRowsOverlay>
                        )),
                        columnSortedAscendingIcon: CustomColumnSortedAscendingIcon,
                        columnSortedDescendingIcon: CustomColumnSortedDescendingIcon,
                    }}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                    }}
                    disableSelectionOnClick
                    selectionModel={selectionModel}
                    onSelectionModelChange={(newSelection) => {
                        setSelectionModel(newSelection);
                    }}
                    sx={{
                        '& .MuiDataGrid-columnHeaders div[role="row"]': {
                            backgroundColor: '#F5F5F7',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            textAlign: 'center',
                            display: 'flex',
                            justifyContent: 'center',
                            width: '100%',
                            fontSize: '14px',
                            fontWeight: '800',
                            color: '#7d7d7d',
                        },
                        '& .MuiDataGrid-cell': {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            fontSize: '16px',
                            color: '#494949',
                            height: '58px',
                            backgroundColor: '#ffffff',
                        },
                        '& .MuiDataGrid-cellContent': {
                            width: '100%',
                        },
                        '& .MuiDataGrid-footerContainer': {
                            display: 'flex',
                            justifyContent: 'center',
                            marginTop: '50px',
                            backgroundColor: '#ffffff',
                        },
                        borderBottomLeftRadius: '14px',  // 왼쪽 아래 모서리 라운드 적용
                        borderBottomRightRadius: '14px', // 오른쪽 아래 모서리 라운드 적용
                        overflow: 'hidden',
                        backgroundColor: '#ffffff',
                    }}
                />
                {/* DeviceManagementDialog Component */}
                <DeviceManagementDialog
                    open={open}
                    onClose={handleClose}
                    rowId={selectedRowId}
                />
                {/* Debug: Check selectedRowId */}
                {console.log('DeviceList render - selectedRowId:', selectedRowId)}
            </Box>
            <Popover
                open={openFilterDialog}
                anchorEl={anchorEl}
                onClose={handleCloseFilterDialog}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    style: {
                        width: 600,
                        height: 429,
                        borderRadius: '8px',
                        border: '1px solid #80befc',
                    },
                }}
            >
                <DialogContent>
                    <form onSubmit={formik.handleSubmit}>
                        <Box sx={{ mt: '10px', ml: '21px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', gap: '24px' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Typography sx={{ color: '#002a70' }}>{t('device_list.filter_search.serial')}</Typography>
                                    <CustomTextField
                                        id="serial"
                                        name="serial"
                                        placeholder={t('device_list.filter_search.serial_placeholder')}
                                        error={false}
                                        disabled={false}
                                        value={formik.values.serial}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        active={true}
                                        size="medium"
                                        width="268px"
                                        height="48px"
                                    />
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: '24px' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Typography sx={{ color: '#002a70' }}>{t('device_list.filter_search.country')}</Typography>
                                    <CustomSelect
                                        id="country"
                                        name="country"
                                        value={formik.values.country}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        menuItems={countryMenuItems}
                                        placeholder={t('device_list.filter_search.country_placeholder')}
                                        width="322px"
                                        height="48px"
                                        itemWidth="303px"
                                        itemHeight="42px"
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Typography sx={{ color: '#002a70' }}>{t('device_list.filter_search.region')}</Typography>
                                    <CustomSelect
                                        id="area"
                                        name="area"
                                        value={formik.values.area}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        menuItems={areaMenuItems}
                                        placeholder={t('device_list.filter_search.region_placeholder')}
                                        width="171px"
                                        height="48px"
                                        itemWidth="151px"
                                        itemHeight="42px"
                                    />
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: '24px' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Typography sx={{ color: '#002a70' }}>{t('device_list.filter_search.reseller')}</Typography>
                                    <CustomSelect
                                        id="reseller"
                                        name="reseller"
                                        value={formik.values.reseller}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        menuItems={resellerMenuItems}
                                        placeholder={t('device_list.filter_search.reseller_placeholder')}
                                        width="322px"
                                        height="48px"
                                        itemWidth="303px"
                                        itemHeight="42px"
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Typography sx={{ color: '#002a70' }}>{t('device_list.filter_search.manager')}</Typography>
                                    <CustomSelect
                                        id="manager"
                                        name="manager"
                                        value={formik.values.manager}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        menuItems={managerMenuItems}
                                        placeholder={t('device_list.filter_search.manager_placeholder')}
                                        width="171px"
                                        height="48px"
                                        itemWidth="151px"
                                        itemHeight="42px"
                                    />
                                </Box>
                            </Box>
                            <Box sx={{ mt: '46px', display: 'flex', justifyContent: 'center', gap: '28px' }}>
                                <Button
                                    onClick={handleCloseFilterDialog}
                                    variant="outlined"
                                    sx={{ color: '#8b8fa8', border: 'solid 1px var(--gray-gray-200)', fontSize: '16px', width: '160px', height: '48px', borderRadius: '10px' }}
                                >
                                    {t('button.cancel')}
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    sx={{ backgroundColor: "#007dfa", fontSize: '16px', width: '160px', height: '48px', borderRadius: '10px' }}
                                >
                                    {t('button.search')}
                                </Button>
                            </Box>
                        </Box>
                    </form>
                </DialogContent>
            </Popover>
        </Box>
    );
};

export default DeviceList;